import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { context, propagation, trace, Context } from '@opentelemetry/api';
import { isUrlAllowedForTracing } from '../../tracing';

/**
 * Injects OpenTelemetry trace context and baggage headers into Angular HttpClient requests.
 *
 * Only injects headers for URLs in the CORS allowlist to prevent:
 * - Baggage leakage to external third-party APIs
 * - CORS preflight failures on services that don't accept custom headers
 * - Security issues with sensitive trace data
 *
 * Merges active trace context with baggage context from APP_INITIALIZER,
 * then uses W3C propagators to inject traceparent and baggage headers.
 *
 * Note: Does NOT create spans manually - auto-instrumentation handles span creation
 * to avoid duplicate spans. This interceptor only handles context propagation.
 */
@Injectable()
export class OtelPropagationInterceptor implements HttpInterceptor {

  /**
   * Intercepts every Angular HttpClient request
   *
   * Flow:
   * 1. Check if request URL is in CORS allowlist
   * 2. If not allowed, pass through without modification
   * 3. If allowed:
   *    a. Get active OpenTelemetry context
   *    b. Merge with baggage context from window
   *    c. Inject traceparent + baggage headers using W3C propagators
   *    d. Clone request with injected headers
   *
   * @param req - Original HTTP request
   * @param next - Next handler in the chain
   * @returns Observable of HTTP events
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Step 1: Special case - Gatekeeper doesn't accept baggage headers
    // Skip header injection entirely for Gatekeeper to avoid CORS errors
    if (req.url.match(/gatekeeper-service(-test)?\.vanguard\.com/i)) {
      return next.handle(req);
    }

    // Step 2: Check if URL is allowed for header injection
    // This prevents CORS issues with external APIs
    if (!isUrlAllowedForTracing(req.url)) {
      // URL not in allowlist - pass through without modification
      return next.handle(req);
    }

    // Step 2: Create a span for this HTTP request
    // This ensures we have trace context to inject
    const tracer = trace.getTracer('angular-http-client');
    const span = tracer.startSpan(`HTTP ${req.method} ${req.url.substring(0, 50)}`, {
      kind: 1, // SpanKind.CLIENT
    });

    // Step 3: Get context with the new span
    const spanContext = trace.setSpan(context.active(), span);

    // Step 4: Merge baggage from window into span context
    // Ensures baggage travels with this request even if context was lost
    const finalContext = this.mergeBaggageContext(spanContext);

    // Step 5: Prepare carrier object for header injection
    const carrier: Record<string, string> = {};

    // Step 6: Inject trace headers using W3C propagators
    // Adds: traceparent, baggage headers to carrier
    propagation.inject(finalContext, carrier);

    // End the span immediately (we only needed it for header injection)
    span.end();

    // Step 6: Clone request with injected headers
    // Angular HttpRequest is immutable, must clone to modify
    const clonedReq = req.clone({
      setHeaders: carrier  // Add traceparent + baggage headers
    });

    // Step 7: Pass cloned request to next handler
    // Auto-instrumentation will create span and set attributes
    return next.handle(clonedReq);
  }

  /**
   * Merge baggage from window context into current context
   *
   * Why needed: Angular HttpClient sometimes loses OpenTelemetry context
   * across async boundaries. Window storage provides a reliable fallback.
   *
   * @param currentContext - Current OpenTelemetry context
   * @returns Context with baggage merged in
   */
  private mergeBaggageContext(currentContext: Context): Context {
    // Get baggage context from window (set in APP_INITIALIZER)
    const windowContext = (window as any).__OTEL_BAGGAGE_CONTEXT__;

    if (!windowContext) {
      // No baggage in window, return current context as-is
      return currentContext;
    }

    // Extract baggage from window context
    const baggage = propagation.getBaggage(windowContext);

    if (!baggage) {
      // No baggage found, return current context as-is
      return currentContext;
    }

    // Set the entire baggage object on the current context
    // propagation.setBaggage() takes 2 args: (context, baggage)
    return propagation.setBaggage(currentContext, baggage);
  }
}
